/**
 * Hook for fetching course data from the Learn Anything API.
 *
 * Replaces hardcoded MOCK_COURSES / MOCK_*_CURRICULUM with real API calls.
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';

interface CourseListItem {
  courseId: string;
  slug: string;
  title: string;
  description: string | null;
  difficultyLevel: string;
  estimatedDurationHours: number | null;
  tags: string[];
  status: string;
}

interface CurriculumLesson {
  lessonId: string;
  title: string;
  lessonType: string;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
}

interface CurriculumModule {
  moduleId: string;
  title: string;
  description: string | null;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
  lessons: CurriculumLesson[];
}

interface Curriculum extends CourseListItem {
  modules: CurriculumModule[];
}

interface LessonProgressEntry {
  correctCount: number;
  targetQuizCount: number;
  status: string;
}

export function useCourseData() {
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  // Fetch published courses on mount
  useEffect(() => {
    let cancelled = false;
    api
      .listCourses()
      .then((res) => {
        if (!cancelled) setCourses(res.courses);
      })
      .catch(() => {
        // Network error — show empty list
      })
      .finally(() => {
        if (!cancelled) setCoursesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchCurriculum = useCallback(async (slug: string): Promise<Curriculum | null> => {
    try {
      return await api.getCourseCurriculum(slug);
    } catch {
      return null;
    }
  }, []);

  const fetchProgress = useCallback(
    async (slug: string): Promise<Record<string, LessonProgressEntry>> => {
      try {
        const res = await api.getCourseProgress(slug);
        return res.lessons ?? {};
      } catch {
        return {};
      }
    },
    [],
  );

  const enrollCourse = useCallback(async (slug: string): Promise<void> => {
    try {
      await api.enrollCourse(slug);
    } catch {
      // Already enrolled or network error — safe to ignore
    }
  }, []);

  return {
    courses,
    coursesLoading,
    fetchCurriculum,
    fetchProgress,
    enrollCourse,
  };
}
